import type { CharterInventoryManagementRow, CharterInventoryManagementUpdatePayload } from '~/services/CharterService';
import { formatDateSlashes } from '~/services/TimeService';

export const INVENTORY_MANAGEMENT_DEPARTING_DEP_AIRPORT = 'MEL';
export const INVENTORY_MANAGEMENT_DEPARTING_ARR_AIRPORT = 'MLE';
export const INVENTORY_MANAGEMENT_RETURNING_DEP_AIRPORT = 'MLE';
export const INVENTORY_MANAGEMENT_RETURNING_ARR_AIRPORT = 'MEL';

export const cabinClassLabel: Record<CharterInventoryManagementRow['cabinClass'], string> = {
  BUSINESS: 'Business',
  ECONOMY: 'Economy',
  LUX_ROW: 'Economy Extra',
  LUX_ROW_PAX_ONE: 'Economy Extra for One',
  PREMIUM_ECONOMY: 'Premium Economy',
};

export const seatingClassLabel: Record<CharterInventoryManagementRow['seatingClass'], string> = {
  business: 'Business',
  economy: 'Economy',
  premium_economy: 'Premium Economy',
};

const seatingClassSortOrder: Record<CharterInventoryManagementRow['seatingClass'], number> = {
  business: 0,
  premium_economy: 1,
  economy: 2,
};

const cabinClassSortOrder: Record<CharterInventoryManagementRow['cabinClass'], number> = {
  BUSINESS: 0,
  PREMIUM_ECONOMY: 1,
  ECONOMY: 2,
  LUX_ROW: 3,
  LUX_ROW_PAX_ONE: 4,
};

export function formatInventoryDate(value: string): string {
  return formatDateSlashes(value) || value;
}

export function formatInventoryRoute(row: Pick<CharterInventoryManagementRow, 'depAirport' | 'arrAirport'>): string {
  return `${row.depAirport} → ${row.arrAirport}`;
}

export function parseAndValidateInventoryRow(row: CharterInventoryManagementRow): CharterInventoryManagementRow {
  const allocation = Number(row.allocation);

  if (!Number.isInteger(allocation) || allocation < 0) {
    throw new Error('Allocation must be a non-negative whole number.');
  }

  return {
    ...row,
    allocation,
    availableSeats: Math.max(allocation - row.booked, 0),
  };
}

export function withComputedInventoryUsage(
  rows: Array<CharterInventoryManagementRow>,
): Array<CharterInventoryManagementRow> {
  const usageByFlightAndSeatingClass = new Map<string, number>();

  for (const row of rows) {
    const key = `${row.flightInstanceId}::${row.seatingClass}`;
    usageByFlightAndSeatingClass.set(
      key,
      (usageByFlightAndSeatingClass.get(key) ?? 0) + row.allocation * row.seatUnitsPerAllocation,
    );
  }

  return rows.map((row) => ({
    ...row,
    availableSeats: Math.max(row.allocation - row.booked, 0),
    seatingClassUsage: usageByFlightAndSeatingClass.get(`${row.flightInstanceId}::${row.seatingClass}`) ?? 0,
  }));
}

export function sortInventoryManagementRows(
  rows: Array<CharterInventoryManagementRow>,
): Array<CharterInventoryManagementRow> {
  return [...rows].sort((left, right) => {
    const dateCompare = left.depDate.localeCompare(right.depDate);
    if (dateCompare !== 0) {
      return dateCompare;
    }

    const timeCompare = left.depTime.localeCompare(right.depTime);
    if (timeCompare !== 0) {
      return timeCompare;
    }

    const routeCompare = formatInventoryRoute(left).localeCompare(formatInventoryRoute(right));
    if (routeCompare !== 0) {
      return routeCompare;
    }

    const seatingCompare = seatingClassSortOrder[left.seatingClass] - seatingClassSortOrder[right.seatingClass];
    if (seatingCompare !== 0) {
      return seatingCompare;
    }

    return cabinClassSortOrder[left.cabinClass] - cabinClassSortOrder[right.cabinClass];
  });
}

export function buildInventoryManagementUpdatePayload(
  editedRows: Map<string, CharterInventoryManagementRow>,
): CharterInventoryManagementUpdatePayload {
  return {
    items: Array.from(editedRows.values()).map((row) => ({
      flightInstanceId: row.flightInstanceId,
      fareFamilyId: row.fareFamilyId,
      allocation: row.allocation,
    })),
  };
}

export function validateInventoryManagementRows(rows: Array<CharterInventoryManagementRow>): Array<string> {
  const errors: Array<string> = [];
  const usageByFlightAndSeatingClass = new Map<
    string,
    { usage: number; capacity: number; row: CharterInventoryManagementRow }
  >();

  for (const row of rows) {
    if (row.allocation < row.booked) {
      errors.push(
        `${formatInventoryDate(row.depDate)} ${formatInventoryRoute(row)} ${cabinClassLabel[row.cabinClass]} allocation must be at least booked seats (${row.booked}).`,
      );
    }

    const key = `${row.flightInstanceId}::${row.seatingClass}`;
    const existing = usageByFlightAndSeatingClass.get(key);
    if (existing) {
      existing.usage += row.allocation * row.seatUnitsPerAllocation;
    } else {
      usageByFlightAndSeatingClass.set(key, {
        usage: row.allocation * row.seatUnitsPerAllocation,
        capacity: row.seatingClassCapacity,
        row,
      });
    }
  }

  for (const { usage, capacity, row } of usageByFlightAndSeatingClass.values()) {
    if (usage > capacity) {
      errors.push(
        `${formatInventoryDate(row.depDate)} ${formatInventoryRoute(row)} ${seatingClassLabel[row.seatingClass]} seating class exceeds capacity (${usage}/${capacity}).`,
      );
    }
  }

  return errors;
}
